import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../context/AuthContext';
import { FeatherX, FeatherPrinter, FeatherDownload } from '../../ui/icons';
import { Button } from '../../ui/button';
import dailyReviewService from '../../../services/dailyReviewService';
import './ProjectReviewReportModal.css';

const ProjectReviewReportModal = ({ projectId, projectName, projectAbbreviation, date, isReviewed, reviewedAt, isOpen, onClose }) => {
    const { user } = useAuth();
    const [summaryData, setSummaryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && projectId && date) {
            fetchReportData();
        }
    }, [isOpen, projectId, date]);

    const fetchReportData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dailyReviewService.getProjectSummary(projectId, date);
            setSummaryData(data);
        } catch (err) {
            console.error('Error fetching report data:', err);
            setError('Failed to load report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePrint = () => {
        // Create a new window with just the report content
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            // If popup blocked, fall back to regular print with CSS hiding
            window.print();
            return;
        }

        const reportContent = document.querySelector('.project-review-report-paper');
        if (!reportContent) {
            printWindow.close();
            return;
        }

        // Get all computed styles for the report
        const styles = `
            @page {
                margin: 1.5cm;
                size: A4;
            }
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            body {
                margin: 0;
                padding: 0;
                font-family: 'Georgia', 'Times New Roman', serif;
                background: #fefefe;
                color: #1a1a1a;
                line-height: 1.8;
            }
            .print-content {
                background: #fefefe !important;
                background-image: 
                    linear-gradient(90deg, transparent 79px, #e5e7eb 81px, #e5e7eb 81px, transparent 83px),
                    linear-gradient(#f5f5f5 0.1em, transparent 0.1em) !important;
                background-size: 100% 1.5em !important;
                background-position: 0 0, 0 0.5em !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                padding: 3rem 4rem 3rem 6rem;
                min-height: 100vh;
            }
            .report-header {
                margin-bottom: 2.5rem;
                padding-bottom: 1.5rem;
                border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                position: relative;
            }
            .report-title {
                font-size: 2.2rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 0.5rem;
                letter-spacing: -0.02em;
                transform: rotate(-0.5deg);
                display: inline-block;
            }
            .report-subtitle {
                font-size: 1.3rem;
                color: #4b5563;
                margin-bottom: 0.75rem;
                font-style: italic;
                transform: rotate(0.3deg);
                display: inline-block;
                margin-left: 0.5rem;
            }
            .report-date {
                font-size: 1rem;
                color: #6b7280;
                margin-top: 0.5rem;
                font-weight: 500;
            }
            .report-status {
                position: absolute;
                top: 0;
                right: 0;
                font-size: 0.95rem;
                color: #059669;
                font-weight: 600;
                transform: rotate(1deg);
                padding: 0.25rem 0.75rem;
                background: rgba(16, 185, 129, 0.1);
                border-radius: 4px;
                border: 1px solid rgba(16, 185, 129, 0.3);
            }
            .report-notes {
                display: flex;
                flex-direction: column;
                gap: 2rem;
            }
            .report-note-section {
                margin-bottom: 1.5rem;
                position: relative;
            }
            .note-section-title {
                font-size: 1.4rem;
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 0.75rem;
                text-decoration: underline;
                text-decoration-thickness: 2px;
                text-underline-offset: 4px;
                transform: rotate(-0.2deg);
                display: inline-block;
                position: relative;
            }
            .note-content {
                margin-left: 1rem;
                font-size: 1.05rem;
                line-height: 2;
            }
            .note-line {
                margin-bottom: 0.5rem;
                position: relative;
                transform: rotate(0.1deg);
                color: #2d2d2d;
            }
            .note-line strong {
                font-weight: 700;
                color: #1a1a1a;
            }
            .note-subtext {
                font-size: 0.95rem;
                color: #6b7280;
                font-style: italic;
                margin-left: 0.25rem;
            }
            .note-indent {
                margin-left: 1.5rem;
                margin-top: 0.25rem;
            }
            .note-resource-group {
                margin-bottom: 1rem;
            }
            .note-resource-name {
                font-weight: 700;
                color: #1a1a1a;
                margin-bottom: 0.25rem;
                transform: rotate(-0.1deg);
                font-size: 1.1rem;
            }
            .note-feature {
                margin-bottom: 0.75rem;
            }
            .feature-name {
                font-weight: 600;
                color: #1a1a1a;
            }
            .note-alert {
                color: #dc2626;
                font-weight: 600;
                font-style: italic;
            }
            .note-warning {
                color: #d97706;
                font-weight: 600;
                font-style: italic;
            }
            .report-footer {
                margin-top: 3rem;
                padding-top: 1.5rem;
                border-top: 1px dashed rgba(0, 0, 0, 0.2);
            }
            .note-meta {
                font-size: 0.9rem;
                color: #6b7280;
                font-style: italic;
                transform: rotate(0.2deg);
            }
            .report-insights {
                background: rgba(255, 251, 235, 0.3);
                border-left: 3px solid #fbbf24;
                padding-left: 1rem;
                margin-bottom: 2rem;
                border-radius: 2px;
            }
            .note-insight {
                font-weight: 600;
                color: #78350f;
                transform: rotate(0.15deg);
            }
            .note-insight::before {
                content: '✓';
                margin-right: 0.5rem;
                color: #059669;
                font-weight: 700;
                transform: rotate(-5deg);
                display: inline-block;
            }
            @media print {
                .print-content {
                    background: #fefefe !important;
                    background-image: 
                        linear-gradient(90deg, transparent 79px, #e5e7eb 81px, #e5e7eb 81px, transparent 83px),
                        linear-gradient(#f5f5f5 0.1em, transparent 0.1em) !important;
                    background-size: 100% 1.5em !important;
                    background-position: 0 0, 0 0.5em !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `;

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Project Review Report - ${projectName || 'Report'}</title>
                <style>${styles}</style>
            </head>
            <body>
                <div class="print-content">
                    ${reportContent.innerHTML}
                </div>
            </body>
            </html>
        `);
        
        printWindow.document.close();
        
        // Wait for content to load, then print
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 250);
    };

    const handleExportPDF = () => {
        // Same as print - user can save as PDF from print dialog
        handlePrint();
    };

    const getQuickInsights = () => {
        if (!summaryData) return null;
        const { pickupStats, unpickedTasksByResource, backlog, featureTags } = summaryData;
        const insights = [];
        
        if (pickupStats?.pickupRate === 100) {
            insights.push('✓ All tasks picked up - Excellent!');
        } else if (pickupStats?.pickupRate >= 80) {
            insights.push('✓ Good pickup rate');
        } else if (pickupStats?.pickupRate < 50) {
            insights.push('⚠ Low pickup rate - needs attention');
        }

        const totalUnpicked = unpickedTasksByResource?.reduce((sum, r) => sum + (r.unpickedCount || 0), 0) || 0;
        if (totalUnpicked > 5) {
            insights.push(`⚠ ${totalUnpicked} tasks unpicked`);
        }

        const overdueFeatures = featureTags?.filter(f => f.status === 'overdue').length || 0;
        if (overdueFeatures > 0) {
            insights.push(`⚠ ${overdueFeatures} feature${overdueFeatures !== 1 ? 's' : ''} overdue`);
        }

        const backlogCount = backlog?.length || 0;
        if (backlogCount > 3) {
            insights.push(`⚠ ${backlogCount} tasks in backlog`);
        }

        return insights;
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="project-review-report-overlay" onClick={onClose}>
            <div 
                className="project-review-report-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="project-review-report-header">
                    <div className="report-header-actions">
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherPrinter size={18} />}
                            onClick={handlePrint}
                            title="Print Report"
                        >
                            Print
                        </Button>
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherDownload size={18} />}
                            onClick={handleExportPDF}
                            title="Export as PDF"
                        >
                            Export
                        </Button>
                    </div>
                    <button 
                        className="project-review-report-close"
                        onClick={onClose}
                        aria-label="Close report"
                    >
                        <FeatherX size={20} />
                    </button>
                </div>

                {/* Report Content */}
                <div className="project-review-report-content">
                    {loading ? (
                        <div className="project-review-report-loading">
                            <p>Loading report...</p>
                        </div>
                    ) : error ? (
                        <div className="project-review-report-error">
                            <p>{error}</p>
                        </div>
                    ) : summaryData ? (
                        <div className="project-review-report-paper">
                            {/* Header */}
                            <div className="report-header">
                                <div className="report-title">{projectName}</div>
                                {projectAbbreviation && (
                                    <div className="report-subtitle">({projectAbbreviation})</div>
                                )}
                                <div className="report-date">{formatDate(date)}</div>
                                {isReviewed && (
                                    <div className="report-status">✓ Reviewed</div>
                                )}
                            </div>

                            {/* Quick Insights */}
                            {getQuickInsights() && getQuickInsights().length > 0 && (
                                <div className="report-note-section report-insights">
                                    <div className="note-section-title">Quick Insights</div>
                                    <div className="note-content">
                                        {getQuickInsights().map((insight, idx) => (
                                            <div key={idx} className="note-line note-insight">
                                                {insight}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Notes Sections */}
                            <div className="report-notes">
                                {/* Pickup Stats */}
                                <div className="report-note-section">
                                    <div className="note-section-title">Task Pickup</div>
                                    <div className="note-content">
                                        <div className="note-line">
                                            Picked up: <strong>{summaryData.pickupStats?.pickedUpToday || 0}</strong> out of <strong>{summaryData.pickupStats?.totalAssigned || 0}</strong> assigned
                                        </div>
                                        <div className="note-line">
                                            Remaining: <strong>{summaryData.pickupStats?.remaining || 0}</strong> tasks
                                        </div>
                                        <div className="note-line">
                                            Pickup rate: <strong>{summaryData.pickupStats?.pickupRate || 0}%</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Tasks Picked Up */}
                                {summaryData.taskDistribution && summaryData.taskDistribution.length > 0 && (
                                    <div className="report-note-section">
                                        <div className="note-section-title">Tasks Picked Up Today</div>
                                        <div className="note-content">
                                            {summaryData.taskDistribution.map((task, idx) => (
                                                <div key={task.taskId} className="note-line">
                                                    • {task.title} ({task.displayId})
                                                    {task.resources && task.resources.length > 0 && (
                                                        <span className="note-subtext">
                                                            {' '}- {task.resources.map(r => r.name).join(', ')}
                                                            {task.totalHours > 0 && ` (${task.totalHours}h)`}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Unpicked Tasks */}
                                {summaryData.unpickedTasksByResource && summaryData.unpickedTasksByResource.length > 0 && (
                                    <div className="report-note-section">
                                        <div className="note-section-title">Unpicked Tasks</div>
                                        <div className="note-content">
                                            {summaryData.unpickedTasksByResource.map((resource) => (
                                                <div key={resource.userId} className="note-resource-group">
                                                    <div className="note-resource-name">{resource.name}:</div>
                                                    {resource.unpickedTasks?.map((task) => (
                                                        <div key={task.taskId} className="note-line note-indent">
                                                            • {task.title} ({task.displayId})
                                                            {task.daysSinceAssignment > 0 && (
                                                                <span className="note-subtext">
                                                                    {' '}- {task.daysSinceAssignment} day{task.daysSinceAssignment !== 1 ? 's' : ''} ago
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Backlog */}
                                {summaryData.backlog && summaryData.backlog.length > 0 && (
                                    <div className="report-note-section">
                                        <div className="note-section-title">Backlog (Unassigned)</div>
                                        <div className="note-content">
                                            {summaryData.backlog.map((task) => (
                                                <div key={task.id} className="note-line">
                                                    • {task.title} ({task.display_id}) - {task.status}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Feature Tags */}
                                {summaryData.featureTags && summaryData.featureTags.length > 0 && (
                                    <div className="report-note-section">
                                        <div className="note-section-title">Feature Tags</div>
                                        <div className="note-content">
                                            {summaryData.featureTags.map((feature) => {
                                                // Calculate active tasks (not completed or released)
                                                const activeTasks = feature.tasks?.filter(t => t.status !== 'completed' && t.status !== 'released') || [];
                                                const isCompleted = activeTasks.length === 0;
                                                
                                                return (
                                                    <div key={feature.id} className="note-feature">
                                                        <div className="note-line">
                                                            <span className="feature-name">{feature.name}</span>
                                                            {feature.status === 'overdue' && (
                                                                <span className="note-alert"> - {feature.days_overdue} day{feature.days_overdue !== 1 ? 's' : ''} overdue!</span>
                                                            )}
                                                            {feature.status === 'due-today' && (
                                                                <span className="note-alert"> - Due today!</span>
                                                            )}
                                                            {feature.status === 'urgent' && (
                                                                <span className="note-warning"> - {feature.days_until_due} day{feature.days_until_due !== 1 ? 's' : ''} left</span>
                                                            )}
                                                            {isCompleted && (
                                                                <span className="note-success"> - Completed</span>
                                                            )}
                                                        </div>
                                                        {activeTasks.length > 0 && (
                                                            <div className="note-indent">
                                                                {activeTasks.map((task) => (
                                                                    <div key={task.id} className="note-line">
                                                                        • {task.title} ({task.display_id}) - {task.status}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Manager Notes */}
                                {summaryData?.reviewNotes && Object.keys(summaryData.reviewNotes).length > 0 && (
                                    <div className="report-note-section">
                                        <div className="note-section-title">Manager Notes</div>
                                        <div className="note-content">
                                            {/* Overall Assessment */}
                                            {summaryData.reviewNotes.overallAssessment && summaryData.reviewNotes.overallAssessment.trim() && (
                                                <div className="note-line">
                                                    <strong>Overall Assessment:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.overallAssessment}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Issues/Concerns */}
                                            {summaryData.reviewNotes.issues && summaryData.reviewNotes.issues.trim() && (
                                                <div className="note-line">
                                                    <strong>Issues/Concerns:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.issues}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Items */}
                                            {summaryData.reviewNotes.actionItems && summaryData.reviewNotes.actionItems.trim() && (
                                                <div className="note-line">
                                                    <strong>Action Items:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.actionItems}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Resource Observations */}
                                            {summaryData.reviewNotes.resourceObservations && summaryData.reviewNotes.resourceObservations.trim() && (
                                                <div className="note-line">
                                                    <strong>Resource Observations:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.resourceObservations}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Task Concerns */}
                                            {summaryData.reviewNotes.taskConcerns && summaryData.reviewNotes.taskConcerns.trim() && (
                                                <div className="note-line">
                                                    <strong>Task Concerns:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.taskConcerns}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Feature Tag Notes */}
                                            {summaryData.reviewNotes.featureTagNotes && summaryData.reviewNotes.featureTagNotes.trim() && (
                                                <div className="note-line">
                                                    <strong>Feature Tag Notes:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.featureTagNotes}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Additional Comments */}
                                            {summaryData.reviewNotes.additionalComments && summaryData.reviewNotes.additionalComments.trim() && (
                                                <div className="note-line">
                                                    <strong>Additional Comments:</strong>
                                                    <div className="note-indent" style={{ whiteSpace: 'pre-wrap' }}>
                                                        {summaryData.reviewNotes.additionalComments}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="report-footer">
                                    <div className="note-line note-meta">
                                        Reviewed by: {user?.displayName || user?.email || 'Unknown'}
                                    </div>
                                    {isReviewed && reviewedAt && (
                                        <div className="note-line note-meta">
                                            Reviewed at: {formatDateTime(reviewedAt)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ProjectReviewReportModal;

